'use client';

import { useTranslation } from '@/lib';
import { cn } from '@ui/lib/utils';
import { LocalizedLink } from '@ui/primitives';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

export interface PaginatorSSRProps {
  first: number;
  rows: number;
  totalRecords: number;
  className?: string;
  showInfo?: boolean;
  baseUrl?: string;
  locale: string;
  queryParamNames?: {
    page: string;
    rows: string;
  };
}

export function PaginatorSSR({
  first,
  rows,
  totalRecords,
  className,
  showInfo = true,
  baseUrl,
  locale,
  queryParamNames = { page: 'page', rows: 'rows' },
}: PaginatorSSRProps) {
  const { t } = useTranslation(locale);
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const paginationInfo = useMemo(() => {
    const pageCount = Math.ceil(totalRecords / rows);
    const currentPage = Math.floor(first / rows) + 1;
    const startRecord = totalRecords > 0 ? first + 1 : 0;
    const endRecord = Math.min(first + rows, totalRecords);

    return {
      pageCount,
      currentPage,
      startRecord,
      endRecord,
      hasNextPage: currentPage < pageCount,
      hasPrevPage: currentPage > 1,
    };
  }, [first, rows, totalRecords]);

  const createPageUrl = (page: number, newRows?: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set(queryParamNames.page, page.toString());

    if (newRows !== undefined) {
      params.set(queryParamNames.rows, newRows.toString());
    }

    const url = baseUrl || pathname;
    return `${url}?${params.toString()}`;
  };

  const renderPageLinks = () => {
    const { pageCount, currentPage } = paginationInfo;
    const links = [];

    if (pageCount <= 7) {
      for (let i = 1; i <= pageCount; i++) {
        links.push(renderPageLink(i));
      }
    } else {
      links.push(renderPageLink(1));

      if (currentPage > 4) {
        links.push(
          <span key="start-ellipsis" className="px-3 py-2 text-gray-500 select-none">
            ...
          </span>
        );
      }

      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(pageCount - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        links.push(renderPageLink(i));
      }

      if (currentPage < pageCount - 3) {
        links.push(
          <span key="end-ellipsis" className="px-3 py-2 text-gray-500 select-none">
            ...
          </span>
        );
      }

      if (pageCount > 1) {
        links.push(renderPageLink(pageCount));
      }
    }

    return links;
  };

  const renderPageLink = (page: number) => {
    const isActive = page === paginationInfo.currentPage;
    const href = createPageUrl(page);

    if (isActive) {
      return (
        <span
          key={page}
          className={cn(
            'px-3 py-2 text-sm font-medium rounded-lg',
            'bg-primary-700 text-white shadow-sm',
            'border border-primary-700'
          )}
          aria-current="page"
        >
          {page}
        </span>
      );
    }

    return (
      <LocalizedLink
        locale={locale}
        key={page}
        href={href}
        className={cn(
          'px-3 py-2 text-sm font-medium rounded-lg transition-colors',
          'text-gray-700 border border-gray-300 bg-white',
          'hover:bg-primary-50 hover:text-primary-700 hover:border-primary-300',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1'
        )}
        prefetch={false}
      >
        {page}
      </LocalizedLink>
    );
  };

  if (totalRecords === 0) {
    return null;
  }

  return (
    <nav className={cn('flex flex-col gap-4 p-4', className)}>
      {showInfo && (
        <div className="text-sm text-gray-600 whitespace-nowrap">
          {t('navigation.showing')} <strong>{paginationInfo.startRecord}</strong> -{' '}
          <strong>{paginationInfo.endRecord}</strong> {t('navigation.of')} <strong>{totalRecords}</strong>{' '}
          {t('navigation.results')}
        </div>
      )}

      {paginationInfo.pageCount > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
          <div className="flex items-center gap-1">
            <LocalizedLink
              locale={locale}
              href={createPageUrl(1)}
              className={cn(
                'flex items-center gap-1 px-2 py-2 text-sm font-medium rounded-lg transition-colors',
                'border border-gray-300 bg-white',
                !paginationInfo.hasPrevPage
                  ? 'text-gray-400 cursor-not-allowed pointer-events-none'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
              prefetch={false}
            >
              <ChevronsLeft className="h-4 w-4" />
              <span className="hidden md:block">{t('navigation.first')}</span>
            </LocalizedLink>
            <LocalizedLink
              locale={locale}
              href={createPageUrl(paginationInfo.currentPage - 1)}
              className={cn(
                'flex items-center gap-1 px-2 py-2 text-sm font-medium rounded-lg transition-colors',
                'border border-gray-300 bg-white',
                !paginationInfo.hasPrevPage
                  ? 'text-gray-400 cursor-not-allowed pointer-events-none'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
              prefetch={false}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden md:block">{t('navigation.previous')}</span>
            </LocalizedLink>
          </div>

          <div className="flex items-center gap-1 overflow-x-auto">{renderPageLinks()}</div>

          <div className="flex items-center gap-1">
            <LocalizedLink
              locale={locale}
              href={createPageUrl(paginationInfo.currentPage + 1)}
              className={cn(
                'flex items-center gap-1 px-2 py-2 text-sm font-medium rounded-lg transition-colors',
                'border border-gray-300 bg-white',
                !paginationInfo.hasNextPage
                  ? 'text-gray-400 cursor-not-allowed pointer-events-none'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
              prefetch={false}
            >
              <span className="hidden md:block">{t('navigation.next')}</span>
              <ChevronRight className="h-4 w-4" />
            </LocalizedLink>
            <LocalizedLink
              locale={locale}
              href={createPageUrl(paginationInfo.pageCount)}
              className={cn(
                'flex items-center gap-1 px-2 py-2 text-sm font-medium rounded-lg transition-colors',
                'border border-gray-300 bg-white',
                !paginationInfo.hasNextPage
                  ? 'text-gray-400 cursor-not-allowed pointer-events-none'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
              prefetch={false}
            >
              <span className="hidden md:block">{t('navigation.last')}</span>
              <ChevronsRight className="h-4 w-4" />
            </LocalizedLink>
          </div>
        </div>
      )}
    </nav>
  );
}
